<template>
    <div class="mt-12 pb-80">
        <div class="flex flex-wrap justify-between">
            <h1
                class="text-2xl font-medium leading-normal tracking-small text-gray-800"
            >
                {{ $t("vue.accounts") }}
            </h1>
            <AtomicButton
                color="yellow"
                size="lg"
                @click="checkAccessOrShowModal() && (createTeamModal = true)"
            >
                <template #icon>
                    <PlusCircleIcon class="mr-2 h-6 w-6" />
                </template>
                {{ $t("vue.create_new_account") }}
            </AtomicButton>
        </div>

        <DataTable class="mt-10" :headers="headers" :items="teams">
            <template #item-profile_photo_url="{ data, row }">
                <div class="flex justify-center py-2">
                    <div
                        class="h-10 w-10 cursor-pointer rounded-full ring-[1px] ring-gray-300"
                    >
                        <img
                            :src="data ? data : row.profile_photo_url"
                            class="h-10 w-10 rounded-full object-cover"
                            alt="Team member photo url"
                        />
                    </div>
                </div>
            </template>

            <template #item-name="{ data, row }">
                <div class="px-6 text-gray-800">
                    {{ data }}
                </div>
            </template>

            <template #item-created_at="{ data }">
                <div class="px-6 text-gray-500">
                    {{ format(new Date(data as string), "d. L. yyyy") }}
                </div>
            </template>

            <template #item-custom="{ row }">
                <div class="flex items-center justify-center">
                    <div v-if="teamProps.id === row.id">
                        <span class="text-xs text-gray-700">{{
                            $t("vue.current")
                        }}</span>
                    </div>
                    <div v-if="teamProps.id !== row.id">
                        <AtomicButton
                            class="mx-4"
                            size="sm"
                            color="white"
                            @click="switchTeam(row.id)"
                        >
                            <span class="text-gray-700">{{
                                $t("vue.switch")
                            }}</span>
                        </AtomicButton>
                    </div>
                </div>
            </template>
        </DataTable>
    </div>

    <CreateTeamModal v-if="createTeamModal" @close="createTeamModal = false" />
    <UpgradeRequiredModal
        :opened="showUpgradeModal"
        title="Scale Plan Required"
        description="Creating new accounts requires a Scale plan subscription. Upgrade your subscription to access team management features."
        @close="closeUpgradeModal"
    />
</template>

<script lang="ts" setup>
import { format } from "date-fns";
import { PlusCircleIcon } from "@heroicons/vue/24/outline";
import type IUser from "@/types/Members/IUser";
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";
import { computed, ref } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";
import useTeamMemberAccess from "@/composables/useTeamMemberAccess";
import UpgradeRequiredModal from "@/components/Atomic/Modals/UpgradeRequiredModal.vue";

defineProps<{
    teams: IPaginationData<{
        id: number;
        name: string;
        personal_team: boolean;
        created_at: string;
    }>;
}>();

const { t } = useTranslations();
const {
    hasTeamMemberAccess,
    showUpgradeModal,
    checkAccessOrShowModal,
    closeUpgradeModal,
} = useTeamMemberAccess();
const createTeamModal = ref(false);

const headers: IDataTableHeader[] = [
    { text: "", id: "profile_photo_url" },
    {
        text: t("vue.name"),
        id: "name",
        sortable: false,
    },
    {
        text: t("vue.created_at"),
        id: "created_at",
        sortable: false,
    },
    { text: "", id: "custom" },
];

const globalProps = computed(() => usePage<IRhizomPageProps>().props);
const userProps = computed(() => globalProps.value.user as IUser);
const teamProps = computed(() => globalProps.value.team as ICurrentTeam);

const form = ref({
    id: {} as number,
});

const switchTeam = (teamId: number) => {
    form.value.id = teamId;
    router.post(route("teams.switch.store"), form.value, {});
};
</script>

<style scoped></style>
